/**
 * Resolves a product's discount display into one of four states, for any surface that shows a
 * price: the catalogue tile and the quick-view drawer both read this so they cannot disagree.
 *
 * Business rules (confirmed by product, 2026-07-15):
 * - Rule 1: brand-funded discounts (fixed product / event / personalized / loyalty / volume)
 *   never add up among themselves — the highest one wins.
 * - Rule 2: exactly one Ankorstore-funded discount ("Extra -X% at checkout") stacks additively
 *   on top of the winning brand-funded discount, on the original price.
 *
 * `guaranteedPercentage` comes from the per-retailer-personalized `discount_rate` (resolved
 * server-side to the winning cart-independent brand-funded discount). The volume/tiered discount
 * is cart-total-dependent, so it is never guaranteed before the cart; the Ankorstore-funded discount
 * applies at checkout, so it is not guaranteed either. Both only ever raise the "Up to" ceiling,
 * never the strikethrough floor.
 */

export enum DiscountState {
  /** No discount — default price, no strikethrough, no label. */
  None = 'none',
  /** Guaranteed fixed discount — purple price, struck original, "{X}% off". */
  Fixed = 'fixed',
  /** Only variable/conditional discounts — default price, no strikethrough, "Up to {X}% off". */
  Variable = 'variable',
  /** Guaranteed fixed + variable — purple price, struck original, "Up to {X}% off". */
  Combined = 'combined',
}

export interface DiscountInput {
  /**
   * Guaranteed, cart-independent brand-funded discount % (from personalized `discount_rate`).
   *
   * IMPORTANT: pass 0 unless there is a concrete discounted price to strike through. A positive
   * `discount_rate` does NOT guarantee a displayable discounted price — the builder returns an
   * undefined `discountedPrice` when the underlying wholesale/variant amount is missing (e.g.
   * logged-out payloads or malformed variant data). Callers should pass
   * `discountedPrice ? discountPercentage : 0` so the guaranteed state never claims a strikethrough
   * the price block cannot render.
   */
  guaranteedPercentage: number;
  /** Whether a cart-total-dependent volume/tiered discount applies to this brand. */
  hasVolumeDiscount: boolean;
  /** Maximum % reachable via the volume/tiered discount (top tier). Ignored when `hasVolumeDiscount` is false. */
  volumeMaxPercentage: number;
  /** Ankorstore-funded "Extra at checkout" discount %. Absent/0 when none. */
  fundedPercentage?: number;
}

export interface DiscountDisplay {
  state: DiscountState;
  /** Whether there is any discount to show. */
  hasDiscount: boolean;
  /** Whether a guaranteed amount exists → drives the purple price + strikethrough. */
  isGuaranteed: boolean;
  /** Whether the final amount is not guaranteed at tile level → "Up to {X}%". */
  isUpTo: boolean;
  /** The guaranteed % (the strikethrough floor). 0 when nothing is guaranteed. */
  guaranteedPercentage: number;
  /** The % shown in the label (the ceiling). */
  displayPercentage: number;
  /**
   * Top volume/tiered tier %, 0 when the brand has none. Reported apart from the ceiling so a surface
   * can itemise "up to X% on volume" separately from the guaranteed rate.
   */
  volumeMaxPercentage: number;
  /**
   * Ankorstore-funded %, 0 when there is no offer. Reported apart from the ceiling so a surface can
   * show it as its own "extra at checkout" line instead of folding it into the strikethrough.
   */
  fundedPercentage: number;
}

/** Clamps a percentage to a safe, non-negative finite number (defaults absent/invalid to 0). */
function toPercentage(value: number | undefined | null): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    return 0;
  }
  return value;
}

export function resolveDiscountState(input: DiscountInput): DiscountDisplay {
  const guaranteed = toPercentage(input.guaranteedPercentage);
  const volumeMax = input.hasVolumeDiscount ? toPercentage(input.volumeMaxPercentage) : 0;
  const funded = toPercentage(input.fundedPercentage);

  // Rule 1 (brand-funded highest-wins) + Rule 2 (one Ankorstore-funded discount, additive on top).
  const ceiling = Math.max(guaranteed, volumeMax) + funded;

  const isGuaranteed = guaranteed > 0;
  // Not guaranteed when a variable/conditional component pushes the total above the guaranteed
  // floor: volume that can exceed it, or the funded discount added on top.
  const isUpTo = ceiling > guaranteed;
  const hasDiscount = ceiling > 0;

  let state: DiscountState;
  if (!hasDiscount) {
    state = DiscountState.None;
  } else if (isGuaranteed && !isUpTo) {
    state = DiscountState.Fixed;
  } else if (!isGuaranteed) {
    state = DiscountState.Variable;
  } else {
    state = DiscountState.Combined;
  }

  // Flags are computed on the raw values; only the reported percentages are rounded. Rounding
  // matters here because — unlike the legacy pipeline, which only ever showed a single raw
  // `discount_rate` — this resolver *combines* values (`max(...) + funded`), which can yield a
  // non-integer when volume/funded percentages are decimals.
  return {
    state,
    hasDiscount,
    isGuaranteed,
    isUpTo,
    guaranteedPercentage: Math.round(guaranteed),
    displayPercentage: Math.round(ceiling),
    volumeMaxPercentage: Math.round(volumeMax),
    fundedPercentage: Math.round(funded),
  };
}
